import { useEffect, useRef, useState } from 'react';
import { getBadges, getMe, getToken, logout as apiLogout, markTermineAlsGesehen, type Praxis, type PraxisBadges } from './api';
import Fragen from './pages/Fragen';
import Inbox from './pages/Inbox';
import Login from './pages/Login';
import MeinePraxis from './pages/MeinePraxis';
import Patienten from './pages/Patienten';
import Termine from './pages/Termine';
import { useIdleLogout } from './useIdleLogout';

type Page =
  | 'krankmeldungen'
  | 'folgerezepte'
  | 'ueberweisungen'
  | 'patienten'
  | 'fragen'
  | 'termine'
  | 'meine-praxis';

const ZERO_BADGES: PraxisBadges = {
  inbox_neu: 0,
  krankmeldungen_neu: 0,
  folgerezepte_neu: 0,
  ueberweisungen_neu: 0,
  fragen_neu: 0,
  termine_offen: 0,
  termine_neue_antwort: 0,
};

export default function App() {
  const [praxis, setPraxis] = useState<Praxis | null>(null);
  const [checking, setChecking] = useState(true);
  const [page, setPage] = useState<Page>('krankmeldungen');
  const [badges, setBadges] = useState<PraxisBadges>(ZERO_BADGES);
  const [idleWarning, setIdleWarning] = useState(false);
  const pollRef = useRef<number | null>(null);

  // DSGVO: Automatischer Logout bei 20 Min Inaktivität (Browser-Tab idle)
  useIdleLogout({
    enabled: Boolean(praxis),
    onWarning: () => setIdleWarning(true),
    onIdle: async () => {
      await apiLogout();
      setPraxis(null);
      setIdleWarning(false);
      alert('Aus Sicherheitsgründen wurden Sie nach 20 Minuten Inaktivität abgemeldet.');
    },
  });

  useEffect(() => {
    const token = getToken();
    if (!token) { setChecking(false); return; }
    getMe()
      .then((r) => setPraxis(r.praxis))
      .catch(() => setPraxis(null))
      .finally(() => setChecking(false));
  }, []);

  // Badge-Polling alle 10 Sekunden, wenn eingeloggt
  useEffect(() => {
    if (!praxis) return;
    const tick = async () => {
      try {
        const r = await getBadges();
        setBadges(r.badges);
        // Tab-Title mit Gesamtzahl, damit man auch in anderen Tabs sieht, dass was los ist
        const total = (r.badges.inbox_neu ?? 0) + r.badges.fragen_neu + r.badges.termine_neue_antwort;
        document.title = total > 0 ? `(${total}) Bertsy Praxis` : 'Bertsy Praxis';
      } catch {
        // ignore
      }
    };
    tick();
    pollRef.current = window.setInterval(tick, 10000);
    return () => {
      if (pollRef.current !== null) window.clearInterval(pollRef.current);
      document.title = 'Bertsy Praxis';
    };
  }, [praxis]);

  if (checking) {
    return (
      <div className="login-overlay">
        <div className="login-box">Wird geladen…</div>
      </div>
    );
  }
  if (!praxis) {
    return <Login onLogin={() => getMe().then((r) => setPraxis(r.praxis))} />;
  }

  const onLogout = async () => {
    await apiLogout();
    setPraxis(null);
  };

  return (
    <div className="layout">
      <aside className="sidebar">
        <div className="brand">
          <div className="brand-logo">B</div>
          <div>
            <div className="brand-name">Bertsy</div>
            <div className="brand-sub">Praxis-Dashboard</div>
          </div>
        </div>

        <nav className="nav">
          <div className="nav-section">Anfragen</div>
          <NavBtn active={page === 'krankmeldungen'} onClick={() => setPage('krankmeldungen')} icon="🤒" label="Krankmeldungen" badge={badges.krankmeldungen_neu ?? badges.inbox_neu} />
          <NavBtn active={page === 'folgerezepte'} onClick={() => setPage('folgerezepte')} icon="💊" label="Folgerezepte" badge={badges.folgerezepte_neu ?? 0} />
          <NavBtn active={page === 'ueberweisungen'} onClick={() => setPage('ueberweisungen')} icon="🔀" label="Überweisungen" badge={badges.ueberweisungen_neu ?? 0} />

          <div className="nav-section" style={{ marginTop: 12 }}>Praxis</div>
          <NavBtn active={page === 'patienten'} onClick={() => setPage('patienten')} icon="👥" label="Patienten" />
          <NavBtn active={page === 'fragen'} onClick={() => setPage('fragen')} icon="💬" label="Fragen" badge={badges.fragen_neu} />
          <NavBtn
            active={page === 'termine'}
            onClick={async () => {
              setPage('termine');
              if (badges.termine_neue_antwort > 0) {
                try {
                  await markTermineAlsGesehen();
                  setBadges((b) => ({ ...b, termine_neue_antwort: 0 }));
                } catch { /* */ }
              }
            }}
            icon="📅"
            label="Termine"
            badge={badges.termine_neue_antwort}
          />
          <NavBtn active={page === 'meine-praxis'} onClick={() => setPage('meine-praxis')} icon="🏥" label="Meine Praxis" />
        </nav>

        <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ fontSize: 11, color: '#5F6B68', padding: '0 8px' }}>angemeldet als</div>
          <div style={{ fontSize: 13, fontWeight: 600, padding: '0 8px' }}>{praxis.name}</div>
          <div style={{ fontSize: 11, color: '#5F6B68', padding: '0 8px', wordBreak: 'break-all' }}>{praxis.email}</div>
          <button className="logout-btn" onClick={onLogout}>Abmelden</button>
        </div>
      </aside>

      <main className="content">
        {page === 'krankmeldungen' && <Inbox fixedTyp="krankmeldung" />}
        {page === 'folgerezepte' && <Inbox fixedTyp="folgerezept" />}
        {page === 'ueberweisungen' && <Inbox fixedTyp="ueberweisung" />}
        {page === 'patienten' && <Patienten />}
        {page === 'fragen' && <Fragen />}
        {page === 'termine' && <Termine />}
        {page === 'meine-praxis' && <MeinePraxis />}
      </main>

      {idleWarning && (
        <div style={{
          position: 'fixed', bottom: 24, right: 24, zIndex: 1000,
          background: '#FFF4E5', border: '1px solid #E99754', borderLeft: '4px solid #E99754',
          padding: '12px 16px', borderRadius: 8, maxWidth: 360,
          boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
        }}>
          <strong style={{ color: '#B85C00' }}>⚠ Bald automatischer Logout</strong>
          <p style={{ fontSize: 13, margin: '4px 0 0', color: '#1D1D1B' }}>
            Aus Datenschutz-Gründen werden Sie in 1 Minute abgemeldet, wenn keine Aktivität stattfindet.
            Bewegen Sie die Maus, um die Session zu verlängern.
          </p>
        </div>
      )}
    </div>
  );
}

function NavBtn({
  active, onClick, icon, label, badge,
}: { active: boolean; onClick: () => void; icon: string; label: string; badge?: number }) {
  return (
    <button className={active ? 'active' : ''} onClick={onClick} style={{ position: 'relative' }}>
      <span className="nav-icon">{icon}</span>
      <span style={{ flex: 1, textAlign: 'left' }}>{label}</span>
      {badge && badge > 0 ? (
        <span className="nav-badge">{badge > 99 ? '99+' : badge}</span>
      ) : null}
    </button>
  );
}
